import {
  Button,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Switch,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { Add16Regular, Checkmark16Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import type { Device, Network, RouteWarning, SiteRegistration } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { errorText } from '../../lib/useRelay';
import { vt } from '../../theme';
import { MAX_ROUTES, defaultSiteNetwork, normaliseClaimCode, routeError } from './model';

const useStyles = makeStyles({
  head: { display: 'flex', alignItems: 'flex-start', padding: '22px 16px 14px 24px', borderBottom: `1px solid ${vt.surface2}` },
  titles: { flexGrow: 1, minWidth: 0 },
  title: { margin: 0, fontSize: '16px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  sub: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11px', color: vt.fg3, marginTop: '4px' },
  steps: { listStyleType: 'none', margin: 0, padding: '18px 24px 6px', display: 'flex', flexDirection: 'column', rowGap: '14px' },
  step: { display: 'grid', gridTemplateColumns: '22px minmax(0, 1fr)', columnGap: '10px', alignItems: 'start' },
  num: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    boxSizing: 'border-box',
    border: `1px solid ${vt.surface2}`,
    backgroundColor: vt.surface3,
    color: vt.fg3,
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  numActive: { backgroundColor: vt.brand, border: 'none', color: tokens.colorNeutralForegroundOnBrand },
  stepName: { fontSize: '12.5px', color: vt.fg2, lineHeight: '22px' },
  stepActive: { color: vt.fg1, fontWeight: tokens.fontWeightSemibold },
  stepNote: { display: 'block', fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg4, lineHeight: '14px' },
  section: { padding: '12px 24px', display: 'flex', flexDirection: 'column', rowGap: '10px' },
  // The claim code is read off a router's console and typed by a human: big, mono, grouped.
  code: { borderRadius: '8px', backgroundColor: vt.brandTint, padding: '14px 16px', display: 'flex', flexDirection: 'column', rowGap: '6px' },
  codeLabel: { fontSize: '11px', color: vt.fg3 },
  codeInput: { fontFamily: tokens.fontFamilyMonospace, fontSize: '20px', '& input': { fontFamily: tokens.fontFamilyMonospace, fontSize: '20px' } },
  found: { fontSize: '11px', color: vt.fg3 },
  label: { fontSize: '11.5px', fontWeight: tokens.fontWeightSemibold, color: vt.fg2 },
  routeRow: { display: 'flex', alignItems: 'center', columnGap: '6px', '& > :first-child': { flexGrow: 1 } },
  mono: { fontFamily: tokens.fontFamilyMonospace },
  ok: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.green },
  bad: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.red },
  add: { alignSelf: 'flex-start', color: vt.brand, minWidth: 0, paddingInline: '4px', fontSize: '11px' },
  divider: { borderTop: `1px solid ${vt.surface2}`, marginTop: '8px' },
  toggleNote: { display: 'block', fontSize: '10px', color: vt.fg4 },
  actions: { display: 'flex', columnGap: '10px', padding: '16px 24px 22px' },
  action: { fontSize: '12.5px', paddingInline: '14px' },
});

interface Props {
  sites: Device[];
  /** null while loading. */
  networks: Network[] | null;
  onClose: () => void;
  onClaimed: (device: Device) => Promise<void>;
}

interface Lookup {
  code: string;
  registration: SiteRegistration | null;
  warnings: RouteWarning[];
  error: string | null;
}

/**
 * Mockup 08's drawer: the router is already flashed and showing a claim code; the admin types it,
 * checks the subnets it detected, picks a network and claims it. The router collects its token on
 * its next poll (every 15 s at first).
 */
export function AddSiteDrawer({ sites, networks, onClose, onClaimed }: Props) {
  const s = useStyles();
  const { api } = useAuth();
  const [code, setCode] = useState('');
  const [lookup, setLookup] = useState<Lookup | null>(null);
  const [routes, setRoutes] = useState<string[]>([]);
  const [network, setNetwork] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canonical = normaliseClaimCode(code);
  const registration = lookup?.code === canonical ? lookup.registration : null;

  useEffect(() => {
    if (!canonical) return;
    let live = true;
    api
      .getClaim(canonical)
      .then(({ registration: r, warnings }) => {
        if (!live) return;
        setLookup({ code: canonical, registration: r, warnings, error: null });
        setRoutes(r.advertisedRoutes.length ? r.advertisedRoutes : ['']);
      })
      .catch((e: unknown) => live && setLookup({ code: canonical, registration: null, warnings: [], error: errorText(e) }));
    return () => {
      live = false;
    };
  }, [api, canonical]);

  useEffect(() => {
    if (network === null && networks) setNetwork(defaultSiteNetwork(sites, networks));
  }, [network, networks, sites]);

  const chosen = routes.map((r) => r.trim()).filter(Boolean);
  const errors = routes.map((r) => (r.trim() ? routeError(r.trim(), null, sites) : null));
  const claimed = registration?.deviceId != null;
  const ready = registration !== null && !claimed && network !== null && errors.every((e) => e === null) && !busy;
  const step = registration ? 4 : 3;

  const hint = (route: string, i: number) => {
    const r = route.trim();
    if (!r) return null;
    const bad = errors[i] ?? lookup?.warnings.find((w) => w.route === r)?.message ?? null;
    if (bad) return <span className={s.bad}>{bad}</span>;
    const detected = registration?.advertisedRoutes.includes(r);
    return <span className={s.ok}>{detected ? 'detected on LAN · no overlap' : 'no overlap'}</span>;
  };

  const claim = async () => {
    if (!registration || !network) return;
    setBusy(true);
    setError(null);
    try {
      const device = await api.claimSite({ claimCode: registration.claimCode, routes: chosen, network });
      await onClaimed(device);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };

  const steps: [string, string | null][] = [
    ['Install the package', 'opkg install vipper-site_*.ipk'],
    ['Power it on', 'It registers and shows a claim code.'],
    ['Enter the claim code', null],
    ['Choose what it advertises', null],
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.titles}>
          <h2 className={s.title}>Add a site</h2>
          <div className={s.sub}>no SSH, no config file</div>
        </div>
        <Tooltip content="Close" relationship="label">
          <Button appearance="subtle" shape="circular" size="small" icon={<Dismiss16Regular />} onClick={onClose} />
        </Tooltip>
      </div>

      <ol className={s.steps}>
        {steps.map(([name, note], i) => (
          <li key={name} className={s.step} aria-current={i + 1 === step ? 'step' : undefined}>
            <span className={mergeClasses(s.num, i + 1 === step && s.numActive)} aria-hidden>
              {i + 1}
            </span>
            <span className={mergeClasses(s.stepName, i + 1 === step && s.stepActive)}>
              {name}
              {note && <span className={s.stepNote}>{note}</span>}
            </span>
          </li>
        ))}
      </ol>

      <div className={s.section}>
        <div className={s.code}>
          <label className={s.codeLabel} htmlFor="claim-code">
            Claim code
          </label>
          <Input
            id="claim-code"
            className={s.codeInput}
            appearance="underline"
            placeholder="VIP-XXXX-XXXX"
            value={code}
            onChange={(_e, data) => setCode(data.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {canonical && lookup?.code !== canonical && <Spinner size="tiny" label="Looking it up" />}
          {canonical && lookup?.code === canonical && lookup.error && <span className={s.bad}>{lookup.error}</span>}
          {registration && (
            <span className={s.found} data-testid="registration">
              {registration.name}
              {registration.model && ` · ${registration.model}`}
              {registration.firmware && ` · ${registration.firmware}`}
              {claimed && ' · already claimed, waiting for the router to collect its token'}
            </span>
          )}
        </div>
      </div>

      {registration && !claimed && (
        <>
          <div className={s.section} role="group" aria-label="Advertise these subnets">
            <span className={s.label}>Advertise these subnets</span>
            {routes.map((r, i) => (
              <div key={i}>
                <div className={s.routeRow}>
                  <Input
                    className={s.mono}
                    value={r}
                    placeholder="192.168.1.0/24"
                    aria-label={`Subnet ${i + 1}`}
                    onChange={(_e, data) => setRoutes((list) => list.map((x, j) => (j === i ? data.value : x)))}
                  />
                  {routes.length > 1 && (
                    <Tooltip content="Remove" relationship="label">
                      <Button
                        appearance="subtle"
                        shape="circular"
                        size="small"
                        icon={<Dismiss16Regular />}
                        onClick={() => setRoutes((list) => list.filter((_x, j) => j !== i))}
                      />
                    </Tooltip>
                  )}
                </div>
                {hint(r, i)}
              </div>
            ))}
            {routes.length < MAX_ROUTES && (
              <Button
                appearance="transparent"
                size="small"
                className={s.add}
                icon={<Add16Regular />}
                onClick={() => setRoutes((list) => [...list, ''])}
              >
                add another subnet
              </Button>
            )}
          </div>

          <div className={s.section}>
            <Field label={{ children: 'Join network', className: s.label }}>
              <Dropdown
                aria-label="Join network"
                value={network ?? ''}
                selectedOptions={network ? [network] : []}
                onOptionSelect={(_e, data) => data.optionValue && setNetwork(data.optionValue)}
                disabled={!networks}
              >
                {(networks ?? []).map((n) => (
                  <Option key={n.id} value={n.id} text={n.id}>
                    {`${n.id} · ${n.cidr}`}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </div>

          <div className={mergeClasses(s.section, s.divider)}>
            <Switch
              disabled
              checked={false}
              label={
                <>
                  Enforce policy on this site
                  <span className={s.toggleNote}>not built yet: the relay enforces the policy for the site&apos;s LAN</span>
                </>
              }
            />
          </div>
        </>
      )}

      {error && (
        <div className={s.section}>
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        </div>
      )}

      <div className={s.actions}>
        <Button
          appearance="primary"
          shape="circular"
          className={s.action}
          icon={<Checkmark16Regular />}
          disabled={!ready}
          onClick={() => void claim()}
        >
          Claim site
        </Button>
        <Button appearance="subtle" shape="circular" className={s.action} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </>
  );
}
